import {useState} from "react";
import {Col, Modal, Row, Table, Typography} from "antd";
import {
	CheckCircleFilled,
	CloseCircleFilled,
	ClockCircleFilled,
	PlayCircleFilled,
	ExclamationCircleFilled,
	RetweetOutlined,
	StopOutlined,
} from "@ant-design/icons";
import {Doughnut} from "react-chartjs-2";
import {Chart as ChartJS, ArcElement, Tooltip, Legend} from "chart.js";
import dayjs from "dayjs";
import Button from "~/components/Button";

ChartJS.register(ArcElement, Tooltip, Legend);

const {Text} = Typography;

const STATUS_CONFIG = [
	{key: 'trained',     label: 'Đã đào tạo',   hex: '#52c41a', IconComp: CheckCircleFilled},
	{key: 'not_trained', label: 'Chưa đào tạo', hex: '#ff4d4f', IconComp: CloseCircleFilled},
	{key: 'upcoming',    label: 'Sắp diễn ra',  hex: '#1677ff', IconComp: ClockCircleFilled},
	{key: 'in_progress', label: 'Đang diễn ra', hex: '#722ed1', IconComp: PlayCircleFilled},
	{key: 'overdue',     label: 'Quá giờ',      hex: '#fa8c16', IconComp: ExclamationCircleFilled},
	{key: 'postponed',   label: 'Dời lịch',     hex: '#faad14', IconComp: RetweetOutlined},
	{key: 'cancelled',   label: 'Đã hủy',       hex: '#8c8c8c', IconComp: StopOutlined},
];

const CHART_COLORS = [
	'#1677ff', '#52c41a', '#722ed1', '#fa8c16', '#ff4d4f',
	'#13c2c2', '#faad14', '#eb2f96', '#2f54eb', '#a0d911',
];

const detailColumns = [
	{
		title: 'STT',
		key: 'stt',
		width: 50,
		render: (_, __, index) => index + 1,
	},
	{
		title: 'Chủ đề đào tạo',
		key: 'topic',
		render: (_, record) => record.topic || '—',
	},
	{
		title: 'Thời gian bắt đầu',
		key: 'startTime',
		width: 170,
		render: (_, record) => record.startTime
			? dayjs.unix(record.startTime).format('HH:mm — DD/MM/YYYY')
			: '—',
	},
];

function EmployeeDetailModal({record, onClose}) {
	if (!record) return null;
	const name = `${record.trainer.firstname} ${record.trainer.lastname}`;
	const items = record.items || [];
	return (
		<Modal
			open
			title={
				<span>
					{name}
					<Text type="secondary" style={{fontWeight: 400, fontSize: 13, marginLeft: 8}}>
						{record.trained} buổi đã đào tạo
					</Text>
				</span>
			}
			onCancel={onClose}
			footer={null}
			width={680}
			destroyOnClose
		>
			<Table
				columns={detailColumns}
				dataSource={items}
				rowKey="id"
				pagination={false}
				size="small"
				scroll={{x: 500}}
				locale={{emptyText: 'Không có dữ liệu'}}
				style={{marginTop: 8}}
			/>
		</Modal>
	);
}

function TrainingReportSummary({statusSummary = {}, trainerSummary = []}) {

	const [selectedRecord, setSelectedRecord] = useState(null);

	const total = STATUS_CONFIG.reduce((sum, {key}) => sum + (statusSummary[key] ?? 0), 0);
	const totalTrained = trainerSummary.reduce((sum, row) => sum + (row.trained ?? 0), 0);

	// Chart by employee (trainer)
	const trainerLabels = trainerSummary.map(row =>
		row.trainer ? `${row.trainer.firstname} ${row.trainer.lastname}` : '—'
	);
	const trainerValues = trainerSummary.map(row => row.trained ?? 0);
	const trainerColors = trainerSummary.map((_, i) => CHART_COLORS[i % CHART_COLORS.length]);

	const chartData = {
		labels: trainerLabels,
		datasets: [{
			data: trainerValues,
			backgroundColor: trainerColors,
			borderWidth: 2,
			borderColor: '#fff',
		}],
	};

	const centerTextPlugin = {
		id: 'centerText',
		afterDraw(chart) {
			const {ctx, chartArea: {left, right, top, bottom}} = chart;
			ctx.save();
			ctx.font = 'bold 26px sans-serif';
			ctx.fillStyle = '#222';
			ctx.textAlign = 'center';
			ctx.textBaseline = 'middle';
			ctx.fillText(String(totalTrained), (left + right) / 2, (top + bottom) / 2);
			ctx.restore();
		},
	};

	const chartOptions = {
		cutout: '62%',
		responsive: true,
		maintainAspectRatio: true,
		plugins: {
			legend: {
				position: 'right',
				labels: {
					generateLabels: (chart) => {
						const {data} = chart;
						return data.labels.map((label, i) => {
							const value = data.datasets[0].data[i];
							const pct = totalTrained > 0 ? Math.round(value / totalTrained * 100) : 0;
							return {
								text: `${label}  ${value} (${pct}%)`,
								fillStyle: data.datasets[0].backgroundColor[i],
								strokeStyle: 'transparent',
								lineWidth: 0,
								hidden: false,
								index: i,
							};
						});
					},
					padding: 8,
					font: {size: 11},
					boxWidth: 10,
					boxHeight: 10,
				},
			},
			tooltip: {
				callbacks: {
					label: (ctx) => {
						const pct = totalTrained > 0 ? Math.round(ctx.raw / totalTrained * 100) : 0;
						return ` ${ctx.label}: ${ctx.raw} buổi (${pct}%)`;
					},
				},
			},
		},
	};

	const employeeColumns = [
		{
			title: 'STT',
			key: 'stt',
			width: 55,
			align: 'center',
			render: (_, __, index) => (
				<Text type="secondary" style={{fontSize: 13}}>{index + 1}</Text>
			),
		},
		{
			title: 'Nhân viên',
			key: 'trainer',
			render: (_, record) => record.trainer
				? `${record.trainer.firstname} ${record.trainer.lastname}`
				: '—',
		},
		{
			title: 'Số buổi đã đào tạo',
			dataIndex: 'trained',
			key: 'trained',
			width: 160,
			align: 'center',
			render: (val) => (
				<span style={{color: '#52c41a', fontWeight: 700, fontSize: 18}}>{val ?? 0}</span>
			),
		},
		{
			title: '',
			key: 'action',
			width: 100,
			align: 'center',
			render: (_, record) => (
				<Button small blue onClick={() => setSelectedRecord(record)}>
					Xem chi tiết
				</Button>
			),
		},
	];

	return (
		<>
			<div style={{marginBottom: 8}}>
				<Text type="secondary" style={{fontSize: 13}}>
					Tổng số lịch đào tạo trong kỳ: <strong style={{color: '#333'}}>{total}</strong>
				</Text>
			</div>

			{/* Status cards */}
			<Row gutter={[10, 10]} style={{marginBottom: 20}}>
				{STATUS_CONFIG.map(({key, label, hex, IconComp}) => (
					<Col key={key} flex="1" style={{minWidth: 120}}>
						<div style={{
							background: '#fff',
							border: '1px solid #f0f0f0',
							borderRadius: 8,
							padding: '12px 14px',
							display: 'flex',
							alignItems: 'center',
							gap: 10,
						}}>
							<div style={{
								width: 40,
								height: 40,
								borderRadius: '50%',
								background: `${hex}18`,
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flexShrink: 0,
							}}>
								<IconComp style={{fontSize: 20, color: hex}} />
							</div>
							<div>
								<div style={{fontSize: 24, fontWeight: 700, color: hex, lineHeight: 1.1}}>
									{statusSummary[key] ?? 0}
								</div>
								<div style={{fontSize: 11, color: '#888', marginTop: 2, whiteSpace: 'nowrap'}}>
									{label}
								</div>
							</div>
						</div>
					</Col>
				))}
			</Row>

			{/* Employee table + Chart */}
			<Row gutter={20} align="stretch">
				<Col lg={16} xs={24} style={{marginBottom: 16}}>
					<div style={{
						background: '#fff',
						border: '1px solid #f0f0f0',
						borderRadius: 8,
						padding: '16px',
						height: '100%',
					}}>
						<div style={{fontSize: 14, fontWeight: 600, marginBottom: 12}}>
							Thống kê theo nhân viên
						</div>
						<Table
							columns={employeeColumns}
							dataSource={trainerSummary}
							rowKey={(r) => r.trainer?.id ?? 0}
							size="small"
							pagination={{
								pageSize: 10,
								showSizeChanger: true,
								showTotal: (t, [f, l]) =>
									`Hiển thị ${f} đến ${l} trong tổng số ${t} nhân viên`,
							}}
							locale={{emptyText: 'Không có dữ liệu'}}
						/>
					</div>
				</Col>
				<Col lg={8} xs={24} style={{marginBottom: 16}}>
					<div style={{
						background: '#fff',
						border: '1px solid #f0f0f0',
						borderRadius: 8,
						padding: '16px',
						height: '100%',
					}}>
						<div style={{fontSize: 14, fontWeight: 600, marginBottom: 16}}>
							Thống kê theo nhân viên
						</div>
						{totalTrained > 0 ? (
							<Doughnut
								data={chartData}
								options={chartOptions}
								plugins={[centerTextPlugin]}
							/>
						) : (
							<div style={{
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								height: 200,
								color: '#bbb',
								fontSize: 13,
							}}>
								Không có dữ liệu
							</div>
						)}
					</div>
				</Col>
			</Row>

			{selectedRecord && (
				<EmployeeDetailModal
					record={selectedRecord}
					onClose={() => setSelectedRecord(null)}
				/>
			)}
		</>
	);
}

export default TrainingReportSummary;
